"use client"

import { useState, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Bell, Moon, Sun, UserIcon, LogOut, Settings, Shield, AlertTriangle } from "lucide-react"

// Proper interfaces untuk type safety
interface SystemStats {
  requestsPerSecond?: number
  avgResponseTime?: number
  systemHealth?: number
  activeAlerts?: number
}

interface DashboardUser {
  username?: string
  email?: string
  role?: string
  avatar?: string
}

interface Notification {
  id: number
  type: "error" | "warning" | "info"
  message: string
  time: string
  read?: boolean
}

interface DashboardHeaderProps {
  theme?: "dark" | "light"
  toggleTheme?: () => void
  systemStats?: SystemStats
  user?: DashboardUser
  onLogout?: () => void
  className?: string
}

export function DashboardHeader({
  theme = "dark",
  toggleTheme,
  systemStats = {},
  user = {},
  onLogout,
  className = "",
}: DashboardHeaderProps) {
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Safe formatting functions
  const formatNumber = useCallback((num: number | undefined | null): string => {
    if (typeof num !== "number" || isNaN(num)) return "0"
    return num.toLocaleString()
  }, [])

  // Safe logout handler dengan proper error handling
  const handleLogout = useCallback(async () => {
    if (isLoggingOut) return

    setIsLoggingOut(true)
    setError(null)

    try {
      // Safe localStorage access
      if (typeof window !== "undefined" && window.localStorage) {
        localStorage.removeItem("gajah_auth_token")
        localStorage.removeItem("user_session")
      }

      // Call custom logout handler if provided
      if (typeof onLogout === "function") {
        await onLogout()
      } else {
        // Default behavior
        if (typeof window !== "undefined") {
          window.location.reload()
        }
      }
    } catch (error) {
      console.error("Logout error:", error)
      setError("Failed to logout. Please try again.")
    } finally {
      setIsLoggingOut(false)
    }
  }, [isLoggingOut, onLogout])

  // Safe theme toggle handler
  const handleThemeToggle = useCallback(() => {
    try {
      if (typeof toggleTheme === "function") {
        toggleTheme()
      }
    } catch (error) {
      console.error("Theme toggle error:", error)
      setError("Failed to toggle theme")
    }
  }, [toggleTheme])

  // Safe system stats dengan default values
  const safeSystemStats = {
    requestsPerSecond: systemStats?.requestsPerSecond || 0,
    avgResponseTime: systemStats?.avgResponseTime || 0,
    systemHealth: systemStats?.systemHealth || 100,
    activeAlerts: systemStats?.activeAlerts || 0,
  }

  // Safe user data dengan default values
  const safeUser = {
    username: user?.username || "Admin",
    email: user?.email || "admin@system.local",
    role: user?.role || "System Administrator",
    avatar: user?.avatar || null,
  }

  // Mock notifications dengan proper typing
  const notifications: Notification[] = [
    {
      id: 1,
      type: "error",
      message: "High bot activity detected",
      time: "2 minutes ago",
      read: false,
    },
    {
      id: 2,
      type: "warning",
      message: "Rate limit threshold reached",
      time: "5 minutes ago",
      read: false,
    },
    {
      id: 3,
      type: "info",
      message: "Campaign A performing well",
      time: "10 minutes ago",
      read: true,
    },
  ]

  const unreadNotifications = notifications.filter((n) => !n.read).length

  // Safe notification rendering
  const renderNotification = useCallback((notification: Notification) => {
    try {
      const notificationStyles = {
        error: "bg-red-900/30 border-red-700/50 text-red-300",
        warning: "bg-yellow-900/30 border-yellow-700/50 text-yellow-300",
        info: "bg-blue-900/30 border-blue-700/50 text-blue-300",
      }

      return (
        <div key={notification.id} className={`p-2 rounded border ${notificationStyles[notification.type]}`}>
          <p className="text-sm">{notification.message}</p>
          <p className="text-xs text-slate-400">{notification.time}</p>
        </div>
      )
    } catch (error) {
      console.error("Error rendering notification:", error)
      return null
    }
  }, [])

  // System status indicator
  const getSystemStatus = useCallback(() => {
    const health = safeSystemStats.systemHealth
    if (health >= 95) return { status: "Healthy", color: "text-green-400", dot: "bg-green-400" }
    if (health >= 80) return { status: "Warning", color: "text-yellow-400", dot: "bg-yellow-400" }
    return { status: "Critical", color: "text-red-400", dot: "bg-red-400" }
  }, [safeSystemStats.systemHealth])

  const systemStatus = getSystemStatus()

  return (
    <div className={`bg-slate-800/50 backdrop-blur-xl rounded-xl p-4 mb-6 border border-slate-700 ${className}`}>
      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-4 w-4 text-red-400" />
            <span className="text-red-400 text-sm">{error}</span>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        {/* Logo & Title */}
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl flex items-center justify-center">
            <span className="text-xl">🐘⚡</span>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Gajah Terbang</h1>
            <p className="text-sm text-slate-400">Advanced Anti-Bot Control Center</p>
          </div>
        </div>

        {/* System Status */}
        <div className="hidden md:flex items-center space-x-6">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full animate-pulse ${systemStatus.dot}`}></div>
            <span className={`text-sm ${systemStatus.color}`}>System {systemStatus.status}</span>
          </div>
          <div className="text-sm text-slate-300">
            <span className="text-purple-400 font-medium font-mono">
              {formatNumber(safeSystemStats.requestsPerSecond)}
            </span>{" "}
            req/s
          </div>
          <div className="text-sm text-slate-300">
            <span className="text-blue-400 font-medium font-mono">
              {formatNumber(safeSystemStats.avgResponseTime)}ms
            </span>{" "}
            avg
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-3">
          {/* Notifications */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="relative text-slate-300 hover:text-white">
                <Bell className="w-5 h-5" />
                {unreadNotifications > 0 && (
                  <Badge className="absolute -top-1 -right-1 w-5 h-5 p-0 bg-red-500 text-xs">
                    {unreadNotifications}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 bg-slate-800 border-slate-700">
              <div className="p-3 border-b border-slate-700">
                <h3 className="font-medium text-white">
                  Notifications
                  {unreadNotifications > 0 && (
                    <Badge className="ml-2 bg-red-500/20 text-red-400 border-red-500/50 text-xs">
                      {unreadNotifications} new
                    </Badge>
                  )}
                </h3>
              </div>
              <div className="p-2 space-y-2 max-h-80 overflow-y-auto">
                {notifications.length > 0 ? (
                  notifications.map(renderNotification).filter(Boolean)
                ) : (
                  <div className="text-center py-4 text-slate-500">
                    <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <div className="text-sm">No notifications</div>
                  </div>
                )}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleThemeToggle}
            className="text-slate-300 hover:text-white"
            disabled={!toggleTheme}
          >
            {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white">
                {safeUser.avatar ? (
                  <img
                    src={safeUser.avatar || "/placeholder.svg"}
                    alt={safeUser.username}
                    className="w-5 h-5 rounded-full"
                    onError={(e) => {
                      e.currentTarget.style.display = "none"
                    }}
                  />
                ) : (
                  <UserIcon className="w-5 h-5" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700 min-w-48">
              <div className="p-3 border-b border-slate-700">
                <p className="font-medium text-white">{safeUser.username}</p>
                <p className="text-sm text-slate-400">{safeUser.role}</p>
                {safeUser.email && <p className="text-xs text-slate-500 mt-1">{safeUser.email}</p>}
              </div>
              <DropdownMenuItem className="text-slate-300 hover:text-white hover:bg-slate-700">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuItem className="text-slate-300 hover:text-white hover:bg-slate-700">
                <Shield className="w-4 h-4 mr-2" />
                Security
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-700" />
              <DropdownMenuItem
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {isLoggingOut ? "Logging out..." : "Logout"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  )
}
